import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Plus, Eye, EyeOff, Copy, Trash2, CheckCircle, Loader2 } from 'lucide-react'

interface ApiKey {
  id: string
  service_name: string
  key_name: string
  key_prefix: string
  is_active: boolean
  created_at: number
  last_used_at?: number
}

const serviceIcons: Record<string, string> = {
  mapbox: '🗺️',
  azure_eventhub: '☁️',
  azuga_eld: '🚛',
  stytch: '🔐',
  custom: '⚙️'
}

const serviceLabels: Record<string, string> = {
  mapbox: 'Mapbox',
  azure_eventhub: 'Azure Event Hub',
  azuga_eld: 'Azuga ELD',
  stytch: 'Stytch Auth',
  custom: 'Custom API'
}

export function ApiKeysSection() {
  const queryClient = useQueryClient()

  // Fetch API keys
  const { data, isLoading } = useQuery({
    queryKey: ['api-keys'],
    queryFn: async () => {
      const res = await fetch('/api/settings/api-keys')
      if (!res.ok) throw new Error('Failed to fetch API keys')
      const data = await res.json()
      return data.api_keys as ApiKey[]
    }
  })

  const apiKeys = data || []

  const [showAddForm, setShowAddForm] = useState(false)
  const [newKey, setNewKey] = useState({
    service_name: 'mapbox',
    key_name: '',
    api_key: ''
  })
  const [showKey, setShowKey] = useState<string | null>(null)
  const [copiedKey, setCopiedKey] = useState<string | null>(null)

  // Create API key mutation
  const createMutation = useMutation({
    mutationFn: async (data: typeof newKey) => {
      const res = await fetch('/api/settings/api-keys', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      })
      if (!res.ok) throw new Error('Failed to create API key')
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] })
      setNewKey({ service_name: 'mapbox', key_name: '', api_key: '' })
      setShowAddForm(false)
    }
  })

  const handleAddKey = () => {
    createMutation.mutate(newKey)
  }

  const handleCopyKey = (keyId: string) => {
    // In production, this would fetch the decrypted key from API
    navigator.clipboard.writeText(`Full key for ${keyId}`)
    setCopiedKey(keyId)
    setTimeout(() => setCopiedKey(null), 2000)
  }

  // Delete API key mutation
  const deleteMutation = useMutation({
    mutationFn: async (keyId: string) => {
      const res = await fetch(`/api/settings/api-keys/${keyId}`, {
        method: 'DELETE'
      })
      if (!res.ok) throw new Error('Failed to delete API key')
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] })
    }
  })

  const handleDeleteKey = (keyId: string) => {
    if (confirm('Are you sure you want to delete this API key? This action cannot be undone.')) {
      deleteMutation.mutate(keyId)
    }
  }

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium">API Keys</h3>
          <p className="text-sm text-muted-foreground">
            Manage your integration API keys. Keys are encrypted at rest.
          </p>
        </div>
        <Button onClick={() => setShowAddForm(!showAddForm)}>
          <Plus className="mr-2 h-4 w-4" />
          Add API Key
        </Button>
      </div>

      {showAddForm && (
        <Card>
          <CardHeader>
            <CardTitle>Add New API Key</CardTitle>
            <CardDescription>
              Add an API key for external service integration
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="service">Service</Label>
              <select
                id="service"
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
                value={newKey.service_name}
                onChange={(e) => setNewKey({ ...newKey, service_name: e.target.value })}
              >
                <option value="mapbox">Mapbox</option>
                <option value="azure_eventhub">Azure Event Hub</option>
                <option value="azuga_eld">Azuga ELD</option>
                <option value="stytch">Stytch</option>
                <option value="custom">Custom API</option>
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="keyName">Key Name</Label>
              <Input
                id="keyName"
                placeholder="e.g., Production, Staging, Development"
                value={newKey.key_name}
                onChange={(e) => setNewKey({ ...newKey, key_name: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="apiKey">API Key</Label>
              <Input
                id="apiKey"
                type="password"
                placeholder="Enter your API key"
                value={newKey.api_key}
                onChange={(e) => setNewKey({ ...newKey, api_key: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                Your key will be encrypted before storage
              </p>
            </div>

            <div className="flex gap-2">
              <Button onClick={handleAddKey} disabled={!newKey.key_name || !newKey.api_key}>
                Save API Key
              </Button>
              <Button variant="outline" onClick={() => setShowAddForm(false)}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="space-y-3">
        {isLoading && (
          <Card>
            <CardContent className="py-8 text-center">
              <Loader2 className="mx-auto h-8 w-8 animate-spin text-muted-foreground" />
              <p className="mt-4 text-muted-foreground">Loading API keys...</p>
            </CardContent>
          </Card>
        )}

        {!isLoading && apiKeys.length === 0 && !showAddForm && (
          <Card>
            <CardContent className="py-8 text-center">
              <p className="text-muted-foreground">No API keys configured</p>
              <Button variant="link" onClick={() => setShowAddForm(true)} className="mt-2">
                Add your first API key
              </Button>
            </CardContent>
          </Card>
        )}

        {apiKeys.map((key) => (
          <Card key={key.id}>
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <div className="text-2xl">{serviceIcons[key.service_name]}</div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold">{key.key_name}</h4>
                      <Badge variant="secondary" className="text-xs">
                        {serviceLabels[key.service_name]}
                      </Badge>
                      {key.is_active && (
                        <Badge variant="outline" className="text-xs">
                          <CheckCircle className="mr-1 h-3 w-3" />
                          Active
                        </Badge>
                      )}
                    </div>
                    <div className="mt-1 flex items-center gap-2">
                      <code className="text-sm text-muted-foreground">
                        {showKey === key.id ? '••••••••••••••••' : key.key_prefix}
                      </code>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowKey(showKey === key.id ? null : key.id)}
                      >
                        {showKey === key.id ? (
                          <EyeOff className="h-3 w-3" />
                        ) : (
                          <Eye className="h-3 w-3" />
                        )}
                      </Button>
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground">
                      Created {formatDate(key.created_at)}
                      {key.last_used_at && ` • Last used ${formatDate(key.last_used_at)}`}
                    </p>
                  </div>
                </div>

                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleCopyKey(key.id)}
                  >
                    {copiedKey === key.id ? (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteKey(key.id)}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  )
}
